import { PROFILE_INSIGHT_WHAT_IS_VIDEO_URL } from '@aibees/elephant/constants/videos';
import { DialogTitle, Icon } from '@aibees/shared/components';
import { CreditUsageType } from '@aibees/shared/definition';
import { useCreditUsageItem } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { memo } from 'react';
import * as SX from './sx';
import type { ProfileInsightExportDialogProps as Props } from './types';

export const ProfileInsightExportDialog = memo(
  ({
    className,
    open,
    onDismiss,
    onExportWithoutProfileInsight,
    onApplyAndExport,
  }: Props) => {
    const { credit } = useCreditUsageItem(
      CreditUsageType.CONTACT_PROFILE_REVEAL
    );

    if (!open) return null;

    return (
      <Dialog
        open={open}
        onClose={onDismiss}
        className={classnames('ProfileInsightExportDialog', className)}
        sx={SX.root}
      >
        <DialogTitle
          title="Export Data Completeness"
          icon="file-csv-outline"
          onClose={onDismiss}
        />

        <DialogContent>
          <Box sx={SX.imgWrapper}>
            <img src="/assets/images/export-data-completeness.svg" />
            <Box sx={SX.videoButtonWrapper}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icon name="play-circle-outline" />}
                href={PROFILE_INSIGHT_WHAT_IS_VIDEO_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                What is Profile Insight?
              </Button>
            </Box>
          </Box>
          <Typography
            variant="body2"
            fontWeight={700}
            color="grey.700"
            mt={1.25}
          >
            Your export includes Profile Insights
          </Typography>
          <Stack gap={3} mt={2.5}>
            <Typography variant="body3" fontWeight={400} color="grey.700">
              Your selected export setup includes fields powered by Profile
              Insights.
            </Typography>
            <Typography variant="body3" fontWeight={400} color="grey.700">
              Profile Insights may use up to{' '}
              <Typography
                component="span"
                variant="body3"
                fontWeight={700}
                color="grey.700"
              >
                {credit} credits
              </Typography>{' '}
              per contact.
            </Typography>
            <Typography variant="body3" fontWeight={400} color="grey.700">
              Without it, these fields will be empty in your CSV.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onExportWithoutProfileInsight} size="small">
            Export without Profile Insights
          </Button>
          <Button
            size="small"
            onClick={onApplyAndExport}
            variant="contained"
            color="primary"
          >
            Apply & Export
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

ProfileInsightExportDialog.displayName = 'ProfileInsightExportDialog';
